import { useMemo } from 'react';
import Button from 'components/commercetools-ui/atoms/button';
import Image, { ImageProps } from 'components/commercetools-ui/atoms/image';
import Link from 'components/commercetools-ui/atoms/link';
import { Reference } from 'types/reference';

export interface SplitHeroProps {
  image: ImageProps;
  title: string;
  subtitle: string;
  ctaLabel?: string;
  ctaReference?: Reference;
  ctaLabel2?: string;
  ctaReference2?: Reference;
  isPriority: boolean;
  imageQuality: number;
}

const SplitHero: React.FC<SplitHeroProps> = ({
  image,
  title,
  subtitle,
  ctaLabel,
  ctaReference,
  ctaLabel2,
  ctaReference2,
  isPriority,
  imageQuality,
}) => {
  const cropFocus = useMemo(() => {
    if (image.gravity?.coordinates) {
      const right = Math.ceil((image.gravity?.coordinates.x / (image.media?.width as number)) * 100);
      const top = Math.ceil((image.gravity?.coordinates.y / (image.media?.height as number)) * 100);

      return `right ${right.toString()}% top ${top.toString()}%`;
    }

    if (image.gravity?.mode === 'center') {
      return 'center';
    }

    return 'initial';
  }, [image]);

  return (
    <section className="w-full px-16 py-24 md:px-48">
      <div className="mx-auto flex max-w-screen-2xl flex-col items-stretch justify-between gap-24 rounded-2xl bg-black px-20 py-24 text-white md:flex-row md:gap-40 md:p-40 lg:gap-56">
        <div className="flex flex-1 flex-col justify-center px-10 py-30 md:p-0">
          <h1 className="text-center text-28 font-bold leading-[32px] md:text-left md:text-44 md:leading-[68px]">
            {title}
          </h1>
          <p className="mt-16 text-center text-18 font-semibold leading-[28px] md:mt-20 md:text-left md:text-20 md:font-medium md:leading-[28px]">
            {subtitle}
          </p>

          {(ctaLabel || ctaLabel2) && (
            <div className="mt-24 flex flex-wrap items-center justify-center gap-16 md:justify-start">
              {ctaLabel && (
                <Link link={ctaReference}>
                  <Button variant="ghost" className="rounded-lg bg-white px-32 py-8 md:px-40 md:py-12">
                    <span className="text-14 font-bold uppercase text-black">{ctaLabel}</span>
                  </Button>
                </Link>
              )}
              {ctaLabel2 && (
                <Link link={ctaReference2}>
                  <Button variant="ghost" className="rounded-lg bg-white px-32 py-8 md:px-40 md:py-12">
                    <span className="text-14 font-bold uppercase text-black">{ctaLabel2}</span>
                  </Button>
                </Link>
              )}
            </div>
          )}
        </div>

        {image && (
          <div className="flex-1 px-20 pb-20 md:p-0">
            <div className="relative aspect-[4/3] w-full overflow-hidden rounded-2xl bg-neutral-900">
              <Image
                {...image}
                alt={title}
                fill
                style={{ objectFit: 'cover', objectPosition: cropFocus }}
                className="rounded-2xl"
                priority={isPriority}
                fetchPriority={isPriority ? 'high' : 'auto'}
                quality={imageQuality}
              />
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default SplitHero;
